import { lazy, Suspense, useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  matchPath,
  type Location,
} from 'react-router-dom';
// MUST stay the first project import in this file, above './hooks': the hook
// reads `useIsDesktop` from that barrel, and several test files mock the
// barrel with `vi.importActual` — a nested import of the mocked module from
// *inside* that call resolves to the actual module, binding the hook to the
// real `useMediaQuery` while `AppRoutes` keeps the mock (viewport-gated
// routing then disagrees with itself; caught by App.test's mobile case).
// Loading the hook first makes its import *trigger* the mock factory instead,
// so both sides resolve to the same mocked `useIsDesktop`.
import { usePrimaryRoutesLocation } from './routes/usePrimaryRoutesLocation';
import { useIsDesktop } from './hooks';
import { EventModal } from './components/modal/EventModal';
import { AuthModal } from './components/modal/AuthModal';
import {
  EventsProvider,
  SavedEventsProvider,
  UserProvider,
  DiscoveryProvider,
  GeolocationProvider,
  ConsentProvider,
  UserAuthProvider,
  CreatorUpdatesProvider,
} from './contexts';
import { BottomTabBar } from './components/layout/BottomTabBar';
import { TopBar } from './components/layout/TopBar';
import { CookieConsent } from './components/consent/CookieConsent';
import { PwaInstallBanner } from './components/pwa/PwaInstallBanner';
import { ReturnVisitRecorder } from './components/analytics/ReturnVisitRecorder';
import { EventVisitRecorder } from './components/analytics/EventVisitRecorder';
import { usePageTracking } from './hooks/usePageTracking';
import { useSsrShellRouteGuard } from './hooks/useSsrShellRouteGuard';
import { UserProtectedRoute } from './components/layout/UserProtectedRoute';
import { isOrganizerHost } from './config/host';
import { ORGANIZER_PATHS } from './routes/organizerPaths';
import {
  TERRITORY_PATHS,
  parsePresetRoute,
  territoryRouteKey,
  requiresTerritoryDocumentNavigation,
} from './routes/territoryPaths';
import { isCategorySlug } from './routes/categorySlugs';
import { parseCategorieParam, territoryCategorySelectionPath } from './utils/categoryLinks';
import { useTerritoryRouteResolution } from './hooks/useTerritoryRouteResolution';
import { HomePage } from './pages/HomePage';
import { EventPage } from './pages/EventPage';
// import { ProfilePage } from './pages/ProfilePage';

// Lazily loaded routes: everything except HomePage and EventPage, the two
// landing/indexed pages that must stay in the entry chunk. EsploraPage pulls
// in Leaflet + marker clustering and is the single biggest win here.
const EsploraPage = lazy(() =>
  import('./pages/EsploraPage').then((m) => ({ default: m.EsploraPage }))
);
const SavedEventsPage = lazy(() =>
  import('./pages/SavedEventsPage').then((m) => ({ default: m.SavedEventsPage }))
);
const ProlocoDashboard = lazy(() =>
  import('./pages/ProlocoDashboard').then((m) => ({ default: m.ProlocoDashboard }))
);
const EventEditPage = lazy(() =>
  import('./pages/EventEditPage').then((m) => ({ default: m.EventEditPage }))
);
const EventCreatePage = lazy(() =>
  import('./pages/EventCreatePage').then((m) => ({ default: m.EventCreatePage }))
);
const SponsorshipPage = lazy(() =>
  import('./pages/SponsorshipPage').then((m) => ({ default: m.SponsorshipPage }))
);
const UserLoginPage = lazy(() =>
  import('./pages/UserLoginPage').then((m) => ({ default: m.UserLoginPage }))
);
const UserSignupPage = lazy(() =>
  import('./pages/UserSignupPage').then((m) => ({ default: m.UserSignupPage }))
);
const AuthCallbackPage = lazy(() =>
  import('./pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage }))
);
const UserProfilePage = lazy(() =>
  import('./pages/UserProfilePage').then((m) => ({ default: m.UserProfilePage }))
);
const UserPasswordResetPage = lazy(() =>
  import('./pages/UserPasswordResetPage').then((m) => ({ default: m.UserPasswordResetPage }))
);
const UserPasswordUpdatePage = lazy(() =>
  import('./pages/UserPasswordUpdatePage').then((m) => ({ default: m.UserPasswordUpdatePage }))
);
const StatsPage = lazy(() => import('./pages/StatsPage').then((m) => ({ default: m.StatsPage })));
const PrivacyPage = lazy(() =>
  import('./pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() =>
  import('./pages/legal/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const CookiePolicyPage = lazy(() =>
  import('./pages/legal/CookiePolicyPage').then((m) => ({ default: m.CookiePolicyPage }))
);
const OrganizerRegistrationPage = lazy(() => import('./pages/OrganizerRegistrationPage'));
const OrganizerBillingPage = lazy(() => import('./pages/OrganizerBillingPage'));

/** Sends GA4 page_view on every route change. */
function PageTracker() {
  usePageTracking();
  return null;
}

/** Categoria e selezioni multiple aprono Esplora; il periodo lo decide soltanto il server.
 *  Il router conserva una risoluzione unica sui preset periodo, anche nei cambi query,
 *  mentre pathname/periodo determinano il montaggio e UTM/categorie non lo cambiano. */
function TerritoryRouteElement() {
  const { pathname, search } = useLocation();
  const preset = parsePresetRoute(pathname, search);
  const key = territoryRouteKey(pathname, search);
  const categorieIds = parseCategorieParam(search);
  const hasCategory = Boolean(preset?.category);
  const categoriesOpenEsplora = hasCategory || categorieIds.length >= 2;
  const normalization =
    !hasCategory && new URLSearchParams(search).has('categorie') && categorieIds.length < 2;
  // Con un candidato periodo il router conserva la risoluzione anche sui cambi categorie query.
  const routerOwnsResolution = Boolean(preset?.periodSlug && !normalization);
  const owned = useTerritoryRouteResolution({ enabled: routerOwnsResolution });
  const esploraShownRef = useRef({ key, shown: categoriesOpenEsplora });
  if (esploraShownRef.current.key !== key) {
    esploraShownRef.current = { key, shown: categoriesOpenEsplora };
  } else if (categoriesOpenEsplora) {
    esploraShownRef.current.shown = true;
  }

  // T012-FE: the reserved `esplora` suffix IS the territorial Esplora
  // surface (plan l.57 — "General territorial Esplora adds /esplora to that
  // base"), matching the shared codec's grammar. Render EsploraPage for
  // /eventi/{a}/esplora and /eventi/{a}/{b}/esplora — but never when the
  // segment before `esplora` is a category slug (the codec rejects that
  // shape; the fall-through below stays byte-identical for it).
  // MUST sit after every hook above: this component is not remounted when
  // the esplora suffix appears/disappears, so returning earlier would
  // unmount `useRef`/`useTerritoryRouteResolution` mid-instance.
  const threeSegment = matchPath('/eventi/:a/:b/esplora', pathname);
  const esploraMatch = threeSegment ?? matchPath('/eventi/:a/esplora', pathname);
  // Only the 3-segment form carries `b`.
  const slugBeforeEsplora = threeSegment?.params.b;
  if (esploraMatch && (slugBeforeEsplora === undefined || !isCategorySlug(slugBeforeEsplora))) {
    return <EsploraPage key={key} />;
  }

  if (normalization && preset) {
    const target =
      categorieIds.length === 1
        ? territoryCategorySelectionPath(
            categorieIds,
            { canonicalPath: preset.basePath },
            { pathname, search }
          )
        : `${pathname}${withoutCategorie(search)}`;
    return requiresTerritoryDocumentNavigation(pathname, search, target) ? (
      <TerritoryDocumentRedirect target={target} />
    ) : (
      <Navigate replace to={target} />
    );
  }

  const supplied = routerOwnsResolution ? owned : undefined;
  // Il clic Tutti durante pending non smonta la pagina già visibile; la risposta resta autoritativa.
  const keepEsploraPending = Boolean(supplied && !supplied.settled && esploraShownRef.current.shown);
  if (supplied && !supplied.settled && !keepEsploraPending) return <RouteLoadingFallback />;
  if (categoriesOpenEsplora || supplied?.resolution?.period || keepEsploraPending) {
    return <EsploraPage key={key} territoryRouteResolution={supplied} />;
  }
  return <HomePage key={key} territoryRouteResolution={supplied} />;
}

function withoutCategorie(search: string): string {
  const params = new URLSearchParams(search);
  params.delete('categorie');
  const query = params.toString();
  return query ? `?${query}` : '';
}

function TerritoryDocumentRedirect({ target }: { target: string }) {
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return <RouteLoadingFallback />;
}

/**
 * Shared Suspense fallback for lazy-loaded routes. Mirrors the spinner style
 * already used by UserProtectedRoute so a chunk load doesn't flash an
 * unstyled blank page.
 */
function RouteLoadingFallback() {
  return (
    <div
      role="status"
      aria-label="Caricamento della pagina"
      aria-busy="true"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        minBlockSize: '100dvh',
        background: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          width: '2rem',
          height: '2rem',
          border: '3px solid var(--color-border)',
          borderTopColor: 'var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * AppRoutes — Renders Routes with desktop-gated backgroundLocation support.
 *
 * Contract for state.backgroundLocation:
 * - When a navigation includes `state.backgroundLocation` (a Location object captured
 *   at click-time, e.g., from an EventCard), this component will render the primary
 *   Routes against THAT background location and overlay a modal Routes tree on top.
 * - Mobile-safety gate: backgroundLocation is honored ONLY when useIsDesktop() === true.
 *   On mobile, state.backgroundLocation is ignored and the route renders as a full page.
 * - Direct URL access (no state) always renders the full page on both viewports
 *   (preserves deep-link / share behavior).
 */
export function AppRoutes() {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  // Shell rule (R15), cross-route half: release a `#ssr-content` left behind
  // by another document — single guard for every public route. Must stay in
  // this body, outside any <Routes>: see `useSsrShellRouteGuard`.
  useSsrShellRouteGuard();
  const state = location.state as { backgroundLocation?: Location } | null;
  // CRITICAL: backgroundLocation effective ONLY on desktop
  const backgroundLocation = (isDesktop && state?.backgroundLocation) || null;

  // Location used for the primary <Routes> tree — the full rule (desktop-gated
  // backgroundLocation, synthetic /esplora for desktop event deep links) lives
  // in `usePrimaryRoutesLocation` so `UserProvider` derives its territory
  // pending flag from the exact same location this tree renders with.
  const primaryRoutesLocation = usePrimaryRoutesLocation();
  const isEventRoute = Boolean(matchPath('/event/:id', location.pathname));
  // Modal overlay shown whenever we're on /event/:id on desktop.
  const showEventModal = isDesktop && isEventRoute;

  // Auth modal: shown on desktop when /accedi or /registrati is reached via
  // internal navigation (backgroundLocation present). Direct visits (no
  // backgroundLocation) still render the standalone full-page route.
  const isLoginRoute = Boolean(matchPath('/accedi', location.pathname));
  const isSignupRoute = Boolean(matchPath('/registrati', location.pathname));
  const showAuthModal = isDesktop && backgroundLocation && (isLoginRoute || isSignupRoute);

  return (
    <>
      {isDesktop && <TopBar />}
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes location={primaryRoutesLocation}>
          <Route path="/" element={<HomePage />} />
          {/* Territory landing routes (T014-FE) — same HomePage, no new
              component (R11). HomePage detects the territory path itself
              and presets the location via /api/territory.
              /eventi/:a/:b/:c, /eventi/:a/:b AND /eventi/:a can ALSO carry a
              category (T011-FE, R9) — TerritoryRouteElement picks HomePage vs
              EsploraPage using the same precedence as the server, including
              the ?categorie= multi-selection on a plain territory path. A
              plain 1-segment path is never a category route. */}
          <Route path={TERRITORY_PATHS.CATEGORY_COMUNE} element={<TerritoryRouteElement />} />
          <Route path={TERRITORY_PATHS.COMUNE} element={<TerritoryRouteElement />} />
          <Route path={TERRITORY_PATHS.REGION_OR_PROVINCE} element={<TerritoryRouteElement />} />
          <Route path="/esplora" element={<EsploraPage />} />
          <Route path="/event/:id" element={<EventPage />} />
          {/* End-user auth routes (Supabase Auth) */}
          <Route path="/accedi" element={<UserLoginPage />} />
          <Route path="/registrati" element={<UserSignupPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/profilo" element={<UserProfilePage />} />
          <Route path="/password-reset" element={<UserPasswordResetPage />} />
          <Route path="/password-update" element={<UserPasswordUpdatePage />} />
          <Route path="/salvati" element={<SavedEventsPage />} />
          {/* Hidden analytics dashboard (unlinked) — PostHog public embed */}
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/termini" element={<TermsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        </Routes>
      </Suspense>
      {showEventModal && (
        <Routes>
          <Route path="/event/:id" element={<EventModal />} />
        </Routes>
      )}
      {showAuthModal && (
        <Routes>
          <Route path="/accedi" element={<AuthModal mode="login" />} />
          <Route path="/registrati" element={<AuthModal mode="signup" />} />
        </Routes>
      )}
    </>
  );
}

/**
 * OrganizerRoutes — the creator/proloco console route tree, served only on
 * the dedicated organizer host (see `isOrganizerHost()`).
 *
 * No TopBar, BottomTabBar, EventModal, or AuthModal — those belong to the
 * public discovery app only. The dashboard, the three event routes, the
 * registration route and the billing route are gated behind
 * `UserProtectedRoute` exactly like `/i-miei-eventi/*` was on the public
 * tree, so an unauthenticated visitor is redirected to `/accedi` instead of
 * rendering a console that then fails every API call. Auth and legal routes
 * stay public. The organizer host has no 404 surface of its own — any
 * unknown path redirects to the dashboard.
 */
export function OrganizerRoutes() {
  // Shell rule (R15), cross-route half (N1): vercel.json's /eventi/* and
  // /event/* rewrites are host-independent and api/ssr.ts does not check the
  // host, so this console can be served a real shell — the `*` -> dashboard
  // <Navigate replace /> below would leave it on top. Same placement rule as
  // AppRoutes: component body, outside any <Routes>.
  useSsrShellRouteGuard();
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route
          path={ORGANIZER_PATHS.DASHBOARD}
          element={
            <UserProtectedRoute>
              <ProlocoDashboard />
            </UserProtectedRoute>
          }
        />
        <Route
          path={ORGANIZER_PATHS.REGISTRATION}
          element={
            <UserProtectedRoute>
              <OrganizerRegistrationPage />
            </UserProtectedRoute>
          }
        />
        <Route
          path={ORGANIZER_PATHS.EVENT_NEW}
          element={
            <UserProtectedRoute>
              <EventCreatePage />
            </UserProtectedRoute>
          }
        />
        <Route
          path={ORGANIZER_PATHS.EVENT_EDIT}
          element={
            <UserProtectedRoute>
              <EventEditPage />
            </UserProtectedRoute>
          }
        />
        <Route
          path={ORGANIZER_PATHS.EVENT_SPONSOR}
          element={
            <UserProtectedRoute>
              <SponsorshipPage />
            </UserProtectedRoute>
          }
        />
        <Route
          path={ORGANIZER_PATHS.BILLING}
          element={
            <UserProtectedRoute>
              <OrganizerBillingPage />
            </UserProtectedRoute>
          }
        />
        {/* End-user auth routes (Supabase Auth) — public */}
        <Route path="/accedi" element={<UserLoginPage />} />
        <Route path="/registrati" element={<UserSignupPage />} />
        <Route path="/password-reset" element={<UserPasswordResetPage />} />
        <Route path="/password-update" element={<UserPasswordUpdatePage />} />
        {/* Legal pages — public */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/termini" element={<TermsPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        {/* No 404 surface on the organizer host — send anything unknown to the dashboard */}
        <Route path="*" element={<Navigate to={ORGANIZER_PATHS.DASHBOARD} replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  // Single switch point: the organizer console must never be reachable from
  // the public app, and vice versa. `isOrganizerHost()` is the only input —
  // no conditional is allowed to leak into the route trees or providers below.
  //
  // NOTE (T010-FE): the old mount-once `initialFilters` bootstrap that read
  // `window.location.search` here was REMOVED as a second App-level URL
  // snapshot. Its behaviour (hydrating `?category|city|search|startDate` on a
  // full page load) is re-hydrated inside `EventsContext` (centralized in the
  // state owner) so the compatible/legacy window behaves exactly as today;
  // T012 replaces it with descriptor-driven filters in v2. See W3 in
  // context-task-T010.md.
  if (isOrganizerHost()) {
    return (
      <BrowserRouter>
        <ConsentProvider>
          <UserAuthProvider>
            <CreatorUpdatesProvider>
              <UserProvider>
                {/* The dashboard's filter UI (SearchBar, CategoryChipRow, useFilters)
                    reads EventsContext transitively, even though no organizer page
                    consumes the public event feed directly. EventsProvider is
                    mounted with enabled=false so the context exists (dashboard
                    renders) but /api/events is never requested on this host. */}
                <EventsProvider enabled={false}>
                  <PageTracker />
                  <OrganizerRoutes />
                  <CookieConsent />
                </EventsProvider>
              </UserProvider>
            </CreatorUpdatesProvider>
          </UserAuthProvider>
        </ConsentProvider>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <ConsentProvider>
        <UserAuthProvider>
          <CreatorUpdatesProvider>
            <UserProvider>
              {/* DiscoveryProvider (T010-FE) sits BELOW UserProvider (reads the
                  saved intent for the central generic-entry replacement) and
                  ABOVE EventsProvider (feeds it the active location + the
                  no-nationwide-interim-fetch gate). Public tree only — the
                  organizer console keeps its local, feed-disabled controls with
                  no discovery provider. */}
              <DiscoveryProvider>
                <GeolocationProvider>
                  <PageTracker />
                  {/* Return-visit observations (T008-FE) — PUBLIC host only
                      (the organizer early-return above never reaches this
                      branch). Renders null; observes the router location and
                      delegates every guard to src/lib/returnVisits.ts. */}
                  <ReturnVisitRecorder />
                  <EventVisitRecorder />
                  <SavedEventsProvider>
                    <EventsProvider>
                      <AppRoutes />
                      <BottomTabBar />
                      <CookieConsent />
                      <PwaInstallBanner />
                    </EventsProvider>
                  </SavedEventsProvider>
                </GeolocationProvider>
              </DiscoveryProvider>
            </UserProvider>
          </CreatorUpdatesProvider>
        </UserAuthProvider>
      </ConsentProvider>
    </BrowserRouter>
  );
}

export default App;
